import { useEffect, useRef, useState } from "react";
import { GiPayMoney } from "react-icons/gi";
import { FiAlertCircle, FiFilter, FiX } from "react-icons/fi";
import * as XLSX from "xlsx";
import BotaoRelatorio from "../../../components/relatorios/BotaoRelatorio";
import CabecalhoRelatorio from "../../../components/relatorios/CabecalhoRelatorio";
import Cartao from "../../../components/relatorios/Cartao";
import EstadoVazio from "../../../components/relatorios/EstadoVazio";
import { api } from "../../../services/api";
import { exportarRelatorioPDF, formatarPeriodoFiltro } from "../../../utils/relatorioPdf";

const RelatorioCustoHectare = () => {
  const [dados, setDados] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");

  const [culturas, setCulturas] = useState([]);
  const [talhoes, setTalhoes] = useState([]);
  const [safras, setSafras] = useState([]);
  
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRefs = {
    talhoes: useRef(null),
    culturas: useRef(null),
    safras: useRef(null)
  };

  const [filtros, setFiltros] = useState({
    talhoes: [],
    culturas: [],
    safras: [],
    fase: "",
    data_inicio: "",
    data_fim: ""
  });

  useEffect(() => {
    carregarOpcoes();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && dropdownRefs[openDropdown]?.current && !dropdownRefs[openDropdown].current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  const carregarOpcoes = async () => {
    try {
        const [culturasRes, talhoesRes, safrasRes] = await Promise.all([
        api.get("/agricultura/culturas/"),
        api.get("/fazendas/talhoes/"),
        api.get("/agricultura/safras/")
        ]);

        setCulturas(culturasRes.data.results || []);
        setTalhoes(talhoesRes.data.results || []);
        setSafras(safrasRes.data.results || []);

    } catch (error) {
        console.error("Erro ao carregar opções:", error);
        setCulturas([]);
        setTalhoes([]);
        setSafras([]);
    }
    };

  const handleMultiSelect = (campo, id) => {
    setFiltros(prev => {
      const current = prev[campo] || [];
      const updated = current.includes(id) 
        ? current.filter(item => item !== id)
        : [...current, id];
      return { ...prev, [campo]: updated };
    });
  };

  const handleSelectAll = (campo, options) => {
    if (Array.isArray(options)) {
      const allIds = options.map(opt => opt.id);
      setFiltros(prev => ({ ...prev, [campo]: allIds }));
    }
  };

  const handleClearAll = (campo) => {
    setFiltros(prev => ({ ...prev, [campo]: [] }));
  };

  const handleChange = (e) => {
    setFiltros({
      ...filtros,
      [e.target.name]: e.target.value
    });
  };

  const buscarDados = async () => {
    setCarregando(true);
    setErro("");

    try {
      const filtrosAtivos = {};

      if (filtros.talhoes && filtros.talhoes.length > 0) {
        filtrosAtivos.talhoes = filtros.talhoes.join(",");
      }
      if (filtros.culturas && filtros.culturas.length > 0) {
        filtrosAtivos.culturas = filtros.culturas.join(",");
      }
      if (filtros.safras && filtros.safras.length > 0) {
        filtrosAtivos.safras = filtros.safras.join(",");
      }
      if (filtros.fase) {
        filtrosAtivos.fase = filtros.fase;
      }
      if (filtros.data_inicio) {
        filtrosAtivos.data_inicio = filtros.data_inicio;
      }
      if (filtros.data_fim) {
        filtrosAtivos.data_fim = filtros.data_fim;
      }
      console.log("🌐 URL completa:", `/relatorios/agricolas/custo-hectare/?${new URLSearchParams(filtrosAtivos)}`);

      const response = await api.get("/relatorios/agricolas/custo-hectare/", {
        params: filtrosAtivos
      });
      console.log("🔍 Dados recebidos do backend:", response.data);

      setDados(response.data);
      setShowModal(true);
    } catch (error) {
      console.error(error);
      setErro("Erro ao gerar o relatório. Verifique os filtros e tente novamente.");
    } finally {
      setCarregando(false);
    }
  };

  const limparFiltros = () => {
    setFiltros({
      talhoes: [],
      culturas: [],
      safras: [],
      fase: "",
      data_inicio: "",
      data_fim: ""
    });
  };

  const exportarExcel = () => {
    if (!dados || dados.length === 0) {
      alert("Nao ha dados para exportar!");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(dados);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Custo por Hectare"
    );

    XLSX.writeFile(workbook, "relatorio_custo_hectare.xlsx");
  };

  const exportarPDF = async () => {
    if (!dados || dados.length === 0) {
      alert("⚠️ Não há dados para exportar!");
      return;
    }

    const headersMap = {
      talhao: "Talhão",
      cultura: "Cultura",
      safra: "Safra",
      area_hectares: "Área (ha)",
      custo_total: "Custo Total (R$)",
      custo_hectare: "Custo/ha (R$)",
    };
    const propertyKeys = Object.keys(dados[0] || {});
    const colunas = propertyKeys.map((key) => ({ key, label: headersMap[key] || key }));

    // Linha de total, calculada como no PDF manual antigo — o utilitário
    // compartilhado não tem esse conceito, então ela entra como mais uma
    // linha de dados (só a coluna 0 recebe o rótulo "TOTAL").
    let totalArea = 0;
    let totalCusto = 0;
    dados.forEach((item) => {
      totalArea += parseFloat(item.area_hectares || 0);
      totalCusto += parseFloat(item.custo_total || 0);
    });
    const linhaTotal = {};
    propertyKeys.forEach((key) => {
      linhaTotal[key] = "";
    });
    linhaTotal[propertyKeys[0]] = "TOTAL";
    if ("area_hectares" in linhaTotal) linhaTotal.area_hectares = totalArea;
    if ("custo_total" in linhaTotal) linhaTotal.custo_total = totalCusto;
    if ("custo_hectare" in linhaTotal) linhaTotal.custo_hectare = totalCusto / totalArea || 0;

    await exportarRelatorioPDF({
      titulo: "Relatório de Custo por Hectare",
      colunas,
      dados: [...dados, linhaTotal],
      periodo: formatarPeriodoFiltro(filtros.data_inicio, filtros.data_fim),
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "left" },
        2: { halign: "left" },
        3: { halign: "right" },
        4: { halign: "right" },
        5: { halign: "right" },
      },
      formatarValor: (valor, coluna) => {
        if (valor === "" || valor === null || valor === undefined) return "";
        if (coluna.key === "area_hectares") return typeof valor === "number" ? valor.toFixed(2) : valor;
        if (coluna.key === "custo_total" || coluna.key === "custo_hectare")
          return `R$ ${parseFloat(valor || 0).toFixed(2)}`;
        return String(valor);
      },
    });
  };

  const imprimir = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Relatorio de Custo por Hectare</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .footer { margin-top: 20px; font-size: 12px; color: #666; text-align: center; }
            @media print {
              body { margin: 0; padding: 20px; }
            }
          </style>
        </head>
        <body>
          <h1>Relatorio de Custo por Hectare</h1>
          <p><strong>Gerado em:</strong> ${new Date().toLocaleString()}</p>
          <p><strong>Total de registros:</strong> ${dados.length}</p>
          <table>
            <thead>
              <tr>
                ${Object.keys(dados[0] || {}).map(key => `<th>${key.replace(/_/g, ' ').toUpperCase()}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${dados.map(item => `
                <tr>
                  ${Object.values(item).map(value => `<td>${value || '-'}</td>`).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="footer">Sistema de Gestao Agropecuaria - Relatorio Gerado Automaticamente</div>
          <script>
            window.onload = () => { window.print(); setTimeout(() => window.close(), 100); }
          <\/script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Componente de Select Multiplo corrigido
  const MultiSelect = ({ label, options, selectedValues, fieldName, refProp }) => {
    // Garantir que options é um array
    const optionsArray = Array.isArray(options) ? options : [];
    const selectedArray = Array.isArray(selectedValues) ? selectedValues : [];

    const getSelectedNames = () => {
      const selected = optionsArray.filter(opt => selectedArray.includes(opt.id));
      if (selected.length === 0) return `Selecione ${label.toLowerCase()}`;
      if (selected.length <= 2) return selected.map(s => s.nome).join(", ");
      return `${selected.length} ${label.toLowerCase()} selecionados`;
    };

    return (
      <div className="relative" ref={refProp}>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          {label}
        </label>
        <div
          onClick={() => setOpenDropdown(openDropdown === fieldName ? null : fieldName)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer focus:ring-2 focus:ring-brand-green focus:border-transparent"
        >
          <span className="text-gray-700">{getSelectedNames()}</span>
          <span className="float-right">▼</span>
        </div>
        
        {openDropdown === fieldName && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
            <div className="p-2 border-b border-gray-200 bg-gray-50">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearAll(fieldName);
                }}
                className="text-sm text-blue-600 hover:text-blue-800 mr-3"
              >
                Limpar todos
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectAll(fieldName, optionsArray);
                }}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Selecionar todos
              </button>
            </div>
            {optionsArray.length === 0 ? (
              <div className="px-3 py-2 text-gray-500 text-sm">
                Nenhuma opção disponível
              </div>
            ) : (
              optionsArray.map((option) => (
                <label
                  key={option.id}
                  className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedArray.includes(option.id)}
                    onChange={() => handleMultiSelect(fieldName, option.id)}
                    className="mr-3 h-4 w-4 text-blue-600 rounded border-gray-300"
                  />
                  <span className="text-gray-700">{option.nome}</span>
                </label>
              ))
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <CabecalhoRelatorio
            icone={GiPayMoney}
            titulo="Relatório de Custo por Hectare"
            descricao="Consulte custos planejados e realizados por talhão"
          />

          {/* Filtros */}
          <Cartao titulo="Filtros" icone={FiFilter} className="mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Talhoes - Multi Select */}
              <MultiSelect
                label="Talhoes"
                options={talhoes}
                selectedValues={filtros.talhoes}
                fieldName="talhoes"
                refProp={dropdownRefs.talhoes}
              />

              {/* Culturas - Multi Select */}
              <MultiSelect
                label="Culturas"
                options={culturas}
                selectedValues={filtros.culturas}
                fieldName="culturas"
                refProp={dropdownRefs.culturas}
              />

              {/* Safras - Multi Select */}
              <MultiSelect
                label="Safras"
                options={safras}
                selectedValues={filtros.safras}
                fieldName="safras"
                refProp={dropdownRefs.safras}
              />

              {/* Fase - Select */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Fase
                </label>
                <select
                  name="fase"
                  value={filtros.fase}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent outline-none transition bg-white"
                >
                  <option value="">Todas as fases</option>
                  <option value="plantio">Plantio</option>
                  <option value="manejo">Manejo</option>
                  <option value="colheita">Colheita</option>
                </select>
              </div>

              {/* Data Inicio */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Data Inicio
                </label>
                <input
                  type="date"
                  name="data_inicio"
                  value={filtros.data_inicio}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent outline-none transition"
                />
              </div>

              {/* Data Fim */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Data Fim
                </label>
                <input
                  type="date"
                  name="data_fim"
                  value={filtros.data_fim}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent outline-none transition"
                />
              </div>
            </div>

            {/* Filtros Ativos - Tags */}
            {((filtros.talhoes && filtros.talhoes.length > 0) || 
              (filtros.culturas && filtros.culturas.length > 0) || 
              (filtros.safras && filtros.safras.length > 0)) && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Filtros Selecionados
                </label>
                <div className="flex flex-wrap gap-2">
                  {filtros.talhoes && filtros.talhoes.map((id) => {
                    const talhao = talhoes.find(t => t.id === id);
                    return talhao ? (
                      <span key={`talhao-${id}`} className="bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                        Talhao: {talhao.nome_codigo}
                        <button
                          onClick={() => handleMultiSelect("talhoes", id)}
                          className="ml-1 text-blue-600 hover:text-blue-800"
                        >
                          ×
                        </button>
                      </span>
                    ) : null;
                  })}
                  {filtros.culturas && filtros.culturas.map((id) => {
                    const cultura = culturas.find(c => c.id === id);
                    return cultura ? (
                      <span key={`cultura-${id}`} className="bg-green-100 text-green-800 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                        Cultura: {cultura.nome}
                        <button
                          onClick={() => handleMultiSelect("culturas", id)}
                          className="ml-1 text-green-600 hover:text-green-800"
                        >
                          ×
                        </button>
                      </span>
                    ) : null;
                  })}
                  {filtros.safras && filtros.safras.map((id) => {
                    const safra = safras.find(s => s.id === id);
                    return safra ? (
                      <span key={`safra-${id}`} className="bg-purple-100 text-purple-800 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                        Safra: {safra.nome}
                        <button
                          onClick={() => handleMultiSelect("safras", id)}
                          className="ml-1 text-purple-600 hover:text-purple-800"
                        >
                          ×
                        </button>
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            {erro && (
              <div className="mt-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
                <FiAlertCircle size={18} className="shrink-0" />
                <span>{erro}</span>
              </div>
            )}

            {/* Botoes */}
            <div className="flex gap-3 mt-6">
              <BotaoRelatorio
                variante="primario"
                onClick={buscarDados}
                carregando={carregando}
                className="flex-1"
              >
                Gerar Relatório
              </BotaoRelatorio>

              <BotaoRelatorio variante="secundario" onClick={limparFiltros}>
                Limpar Filtros
              </BotaoRelatorio>
            </div>
          </Cartao>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div 
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4" 
          style={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            overflow: 'auto'
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl my-8 flex flex-col"
            style={{
              zIndex: 10000,
              maxHeight: 'calc(100vh - 4rem)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <GiPayMoney size={22} className="text-brand-green" />
                Relatório de Custo por Hectare
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center transition flex-shrink-0"
              >
                <FiX size={18} className="text-gray-600" />
              </button>
            </div>

            {/* Acoes */}
            <div className="p-6 border-b border-gray-200 bg-gray-50 flex flex-wrap gap-3 flex-shrink-0">
              <BotaoRelatorio variante="excel" onClick={exportarExcel}>
                Exportar para Excel
              </BotaoRelatorio>

              <BotaoRelatorio variante="pdf" onClick={exportarPDF}>
                Exportar para PDF
              </BotaoRelatorio>

              <BotaoRelatorio variante="imprimir" onClick={imprimir}>
                Imprimir
              </BotaoRelatorio>
            </div>

            {/* Tabela */}
            <div className="flex-1 overflow-auto p-6" style={{ minHeight: 0 }}>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6 flex flex-wrap justify-between items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-emerald-800 font-medium">Total de registros:</span>
                  <span className="text-emerald-900 font-bold text-lg">{dados.length}</span>
                </div>
                <span className="text-emerald-800 text-sm">
                  Gerado em: {new Date().toLocaleString("pt-BR")}
                </span>
              </div>

              {dados.length > 0 ? (
                <div className="overflow-x-auto rounded-lg border border-gray-200" style={{ maxWidth: '100%' }}>
                  <div style={{ minWidth: 'max-content' }}>
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200 sticky top-0">
                          {Object.keys(dados[0] || {}).map((key) => (
                            <th key={key} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                              {key.replace(/_/g, " ").toUpperCase()}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {dados.map((item, index) => (
                          <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition">
                            {Object.values(item).map((value, idx) => (
                              <td key={idx} className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                                {value || "-"}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <EstadoVazio descricao="Ajuste os filtros selecionados e tente novamente." />
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end flex-shrink-0">
              <BotaoRelatorio variante="secundario" onClick={() => setShowModal(false)}>
                Fechar
              </BotaoRelatorio>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RelatorioCustoHectare;